import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ResultsPanel } from "@/components/campaigns/ResultsPanel";
import { CreationChat } from "@/components/chat/CreationChat";
import { ExpandedChat } from "@/components/chat/ExpandedChat";
import { HeroPrompt } from "@/components/chat/HeroPrompt";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { CampaignPanel } from "./CampaignPanel";
import { AppSidebar } from "./IconSidebar";

interface CampaignSummary {
  total: number;
  pending: number;
  success: number;
  failed: number;
  ambiguous: number;
}

interface CampaignDetail {
  id: string;
  name: string;
  goal: string | null;
  status: string;
  profileId: string | null;
  createdAt: string;
  updatedAt: string;
  locationSummary: CampaignSummary;
}

export function AppLayout() {
  const search = useSearch({ from: "/dashboard" });
  const navigate = useNavigate();

  const campaignId = search.campaign ?? null;
  const view = (search.view ?? "table") as "table" | "map" | "charts";
  // The campaigns section shows the list + results; a selected campaign implies
  // it. Otherwise we're on Home (the Atlas hero).
  const inCampaigns = campaignId !== null || search.section === "campaigns";

  const [campaign, setCampaign] = useState<CampaignDetail | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [chatMode, setChatMode] = useState<"collapsed" | "expanded" | "split">("collapsed");
  const [creationMode, setCreationMode] = useState(false);
  const [initialMessage, setInitialMessage] = useState("");

  const handleMinimizeChat = useCallback(() => setChatMode("collapsed"), []);
  const handleToggleSplit = useCallback(() => {
    setChatMode((prev) => (prev === "split" ? "expanded" : "split"));
  }, []);

  // Fetch campaign details when campaignId or refreshKey changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: refreshKey intentionally triggers re-fetch
  useEffect(() => {
    if (!campaignId) {
      setCampaign(null);
      return;
    }

    let mounted = true;
    async function fetchCampaign() {
      try {
        const res = await fetch(`/api/campaigns/${campaignId}`);
        if (!res.ok) throw new Error("Failed to fetch");
        const data = await res.json();
        if (mounted) setCampaign(data);
      } catch {
        if (mounted) setCampaign(null);
      }
    }

    fetchCampaign();
    return () => {
      mounted = false;
    };
  }, [campaignId, refreshKey]);

  // Reset chat mode when campaign is deselected
  useEffect(() => {
    if (!campaignId) setChatMode("collapsed");
  }, [campaignId]);

  // Auto-collapse split on narrow viewports
  useEffect(() => {
    function handleResize() {
      if (window.innerWidth < 1024 && chatMode === "split") {
        setChatMode("expanded");
      }
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [chatMode]);

  const handleSelectCampaign = useCallback(
    (id: string) => {
      navigate({
        to: "/dashboard",
        search: { section: "campaigns", campaign: id, view: "table" },
      });
    },
    [navigate],
  );

  const handleViewChange = useCallback(
    (newView: string) => {
      navigate({
        to: "/dashboard",
        search: {
          section: "campaigns",
          campaign: campaignId ?? undefined,
          view: newView as "table" | "map" | "charts",
        },
      });
    },
    [navigate, campaignId],
  );

  const handleRefresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  const handleStartCreation = useCallback((text: string) => {
    setInitialMessage(text);
    setCreationMode(true);
  }, []);

  const handleCancelCreation = useCallback(() => {
    setCreationMode(false);
    setInitialMessage("");
  }, []);

  const handleViewResults = useCallback(
    (newCampaignId: string) => {
      setCreationMode(false);
      setInitialMessage("");
      handleSelectCampaign(newCampaignId);
      handleRefresh();
    },
    [handleSelectCampaign, handleRefresh],
  );

  const handleNewCampaign = useCallback(() => {
    navigate({ to: "/dashboard", search: { section: "home" } });
    setCreationMode(false);
    setChatMode("collapsed");
  }, [navigate]);

  const handleSideEffect = useCallback(
    (action: Record<string, unknown> & { type: string }) => {
      switch (action.type) {
        case "switch_tab":
          handleViewChange(String(action.tab ?? ""));
          break;
        case "apply_filter":
          break;
        case "refresh_data":
          setRefreshKey((k) => k + 1);
          break;
        case "trigger_export":
          break;
        case "prompt_upload":
          toast.info(
            typeof action.message === "string" ? action.message : "Please upload a CSV file",
          );
          break;
      }
    },
    [handleViewChange],
  );

  return (
    <SidebarProvider defaultOpen={false}>
      <AppSidebar />
      <SidebarInset className="h-svh overflow-hidden">
        <div className="flex h-full">
          {/* Campaigns section: list panel (even with nothing selected yet).
              Home takes the full width — recent campaigns live in the hero. */}
          {inCampaigns && (
            <CampaignPanel
              activeCampaignId={campaignId}
              onSelectCampaign={handleSelectCampaign}
              refreshKey={refreshKey}
              onNewCampaign={handleNewCampaign}
            />
          )}

          {/* Home: Atlas hero (or the creation flow once a prompt is sent) */}
          {!inCampaigns &&
            chatMode === "collapsed" &&
            (creationMode ? (
              <CreationChat
                initialMessage={initialMessage}
                onViewResults={handleViewResults}
                onCancel={handleCancelCreation}
                onCampaignCreated={handleRefresh}
              />
            ) : (
              <HeroPrompt
                onSubmit={handleStartCreation}
                onSelectCampaign={handleSelectCampaign}
                refreshKey={refreshKey}
              />
            ))}

          {/* Campaigns section, chat collapsed: results (ResultsPanel shows a
              "select a campaign" empty state when none is selected). */}
          {inCampaigns && chatMode === "collapsed" && (
            <ResultsPanel
              campaignId={campaignId}
              profileId={campaign?.profileId ?? null}
              campaignName={campaign?.name ?? ""}
              locationCount={campaign?.locationSummary?.total ?? 0}
              view={view}
              onViewChange={handleViewChange}
              refreshKey={refreshKey}
            />
          )}

          {/* Chat expanded */}
          {chatMode === "expanded" && campaignId && (
            <ExpandedChat
              campaignId={campaignId}
              campaignName={campaign?.name ?? ""}
              mode="expanded"
              onMinimize={handleMinimizeChat}
              onToggleSplit={handleToggleSplit}
              onSideEffect={handleSideEffect}
            />
          )}

          {/* Chat split */}
          {chatMode === "split" && campaignId && (
            <div className="flex flex-1 overflow-hidden">
              <div className="w-1/2 border-r border-border overflow-hidden">
                <ExpandedChat
                  campaignId={campaignId}
                  campaignName={campaign?.name ?? ""}
                  mode="split"
                  onMinimize={handleMinimizeChat}
                  onToggleSplit={handleToggleSplit}
                  onSideEffect={handleSideEffect}
                />
              </div>
              <div className="w-1/2 overflow-hidden">
                <ResultsPanel
                  campaignId={campaignId}
                  profileId={campaign?.profileId ?? null}
                  campaignName={campaign?.name ?? ""}
                  locationCount={campaign?.locationSummary?.total ?? 0}
                  view={view}
                  onViewChange={handleViewChange}
                  refreshKey={refreshKey}
                />
              </div>
            </div>
          )}
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
